import React from "react";
import { Link } from "react-router-dom";

const legalUpdated = process.env.REACT_APP_LEGAL_LAST_UPDATED?.trim();
const publicEmail = process.env.REACT_APP_CONTACT_EMAIL?.trim();

export default function Terms() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold mb-2">Terms & Conditions</h1>
      <p className="text-sm text-muted-foreground mb-8">{legalUpdated ? `Last updated: ${legalUpdated}` : "Publication date pending legal approval"}</p>

      <div className="prose prose-lg max-w-none space-y-6 text-muted-foreground">
        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">1. Acceptance of Terms</h2>
          <p>
            By accessing or using our platform, you agree to be bound by these Terms and Conditions. 
            If you do not agree, please do not use our services.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">2. User Accounts</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>You must provide accurate and complete registration information</li>
            <li>You are responsible for maintaining the security of your account</li>
            <li>One person may not maintain more than one account</li>
            <li>You must be 18 years or older to create an account</li>
            <li>Admin reserves the right to suspend or terminate accounts that violate these terms</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">3. Property Listings</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Supported buyer, owner, agent, and office accounts may submit listings under the platform workflow</li>
            <li>All listings are subject to admin approval before publication</li>
            <li>Listings must contain accurate, truthful information</li>
            <li>Misleading or fraudulent listings will be removed and may result in account termination</li>
            <li>We reserve the right to edit or remove any listing</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">4. User Roles & Responsibilities</h2>
          <p><strong>Buyers:</strong> May browse, save, compare properties, request visits, submit eligible reviews, and submit listings where enabled.</p>
          <p><strong>Agents:</strong> May list properties, manage appointments, and respond to inquiries. Must maintain accurate credentials.</p>
          <p><strong>Offices:</strong> Have all agent capabilities plus can manage multiple agents and their listings.</p>
          <p><strong>Admin:</strong> Has full platform control including user management, approvals, and content moderation.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">5. Appointments</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Appointment bookings are requests — agents may accept or decline</li>
            <li>Cancellations should be made at least 24 hours in advance</li>
            <li>Repeated no-shows may result in account restrictions</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">6. Reviews & Ratings</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Eligible verified buyer or owner accounts may review a published property, subject to moderation</li>
            <li>Reviews must be honest and based on actual experience</li>
            <li>Fake, defamatory, or abusive reviews will be removed</li>
            <li>Rating scale is 1-5 stars</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">7. Intellectual Property</h2>
          <p>
            All content, design, logos, and technology on this platform are our intellectual property. 
            Property images uploaded by agents remain their property but grant us a license to display them.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">8. Limitation of Liability</h2>
          <p>
            We are a platform connecting property seekers with agents. We do not guarantee the accuracy 
            of listings, property conditions, or transaction outcomes. Users transact at their own risk.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">9. Prohibited Activities</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Scraping or automated data collection</li>
            <li>Impersonating other users or agents</li>
            <li>Posting spam or unsolicited commercial content</li>
            <li>Attempting to bypass security measures</li>
            <li>Using the platform for money laundering or illegal transactions</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">10. Modifications</h2>
          <p>
            We may update these terms at any time. Continued use after changes constitutes acceptance. 
            Significant changes will be communicated via email or platform notification.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">11. Contact</h2>
          <p>
            For questions about these terms, {publicEmail ? <a href={`mailto:${publicEmail}`} className="text-primary hover:underline">contact {publicEmail}</a> : <Link to="/contact" className="text-primary hover:underline">use the contact form</Link>}.
          </p>
        </section>
      </div>
    </div>
  );
}
